import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { authApi } from '../api/endpoints';
import { setUnauthorizedHandler, TOKEN_KEY, USER_KEY } from '../api/client';

const AuthContext = createContext(null);

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth должен вызываться внутри AuthProvider');
    return ctx;
};

/**
 * Хранит текущего пользователя админки.
 *
 * Прежняя схема проверяла только наличие ключа user-info в localStorage, а checkToken
 * при любой сетевой ошибке делал localStorage.clear() + reload — моргнувший бэкенд
 * выкидывал из панели. Здесь разлогинивание происходит только на явный 401.
 */
export const AuthProvider = ({ children }) => {
    const history = useHistory();
    const [user, setUser] = useState(() => localStorage.getItem(USER_KEY) || null);
    const [checking, setChecking] = useState(() => Boolean(localStorage.getItem(TOKEN_KEY)));

    const clearSession = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
    }, []);

    useEffect(() => {
        setUnauthorizedHandler(() => {
            setUser(null);
            history.push('/admin/login');
        });
        return () => setUnauthorizedHandler(null);
    }, [history]);

    // Разовая проверка токена при старте: истёкший токен нужно обнаружить
    // до того, как пользователь начнёт заполнять форму.
    useEffect(() => {
        let cancelled = false;

        if (!localStorage.getItem(TOKEN_KEY)) {
            setChecking(false);
            return undefined;
        }

        authApi
            .me()
            .then((me) => {
                if (cancelled) return;
                if (me && me.name) {
                    localStorage.setItem(USER_KEY, me.name);
                    setUser(me.name);
                }
            })
            .catch((error) => {
                // 401 уже обработан интерцептором. Остальное (сеть, 500) не должно
                // выбрасывать из панели — работаем с тем, что есть в localStorage.
                if (cancelled) return;
                if (error.status === 401) clearSession();
            })
            .finally(() => {
                if (!cancelled) setChecking(false);
            });

        return () => {
            cancelled = true;
        };
    }, [clearSession]);

    const login = useCallback(async (email, password) => {
        const data = await authApi.login(email, password);
        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.setItem(USER_KEY, data.user.name);
        setUser(data.user.name);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            // Отзываем токен на сервере — раньше он оставался жить в базе навсегда.
            await authApi.logout();
        } catch (e) {
            // Даже если запрос не прошёл, локальную сессию всё равно закрываем.
        }
        clearSession();
        history.push('/admin/login');
    }, [clearSession, history]);

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: Boolean(user), checking, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
